import React from 'react';
import { useIntl } from 'react-intl';

import { queryClient } from 'src/lib/react-query';
import ConfirmationModal from 'src/registry/components/blocks/ConfirmationModal';
import { DataTableNewEntryProps } from 'src/registry/components/table/types';
import { toast } from 'src/hooks/useToast';
import { ICON_MAP } from 'src/utils/constants';
import { useOnError } from 'src/utils/errors';
import { useDeleteSubscriptionItemUsageRecord } from 'src/features/subscriptionItemUsageRecords/api/useDeleteSubscriptionItemUsageRecord';
import { LIST_SUBSCRIPTION_ITEM_USAGE_RECORD_RESOURCE } from 'src/features/subscriptionItemUsageRecords/api/useListSubscriptionItemUsageRecord';
import { subItemUsageRecordMessages } from 'src/features/subscriptionItemUsageRecords/utils/messages';
import { SubscriptionItemUsageRecord } from 'src/features/subscriptionItemUsageRecords/utils/types';

interface DeleteSubscriptionItemUsageRecordFormModalProps<TData> extends DataTableNewEntryProps<TData> {
    selectedUsageRecord: SubscriptionItemUsageRecord;
    children?: React.ReactNode;
}

function DeleteSubscriptionItemUsageRecordFormModal<TData>({
    selectedUsageRecord,
    children,
}: DeleteSubscriptionItemUsageRecordFormModalProps<TData>) {
    const [isModalOpen, setIsModalOpen] = React.useState(false);
    const { formatMessage } = useIntl();
    const onError = useOnError();

    const { mutate: deleteUsageRecord } = useDeleteSubscriptionItemUsageRecord({
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: [LIST_SUBSCRIPTION_ITEM_USAGE_RECORD_RESOURCE] });
            setIsModalOpen(false);
            toast({
                title: formatMessage(subItemUsageRecordMessages.deleteUsageRecordSuccess),
                description: formatMessage(subItemUsageRecordMessages.deleteUsageRecordSuccessDescription),
            });
        },
        onError: (error) => {
            onError(error, () => null);
        },
    });

    return (
        <ConfirmationModal
            isModalOpen={isModalOpen}
            setIsModalOpen={setIsModalOpen}
            dialogDetails={{
                title: { ...subItemUsageRecordMessages.deleteUsageRecord },
                subTitle: { ...subItemUsageRecordMessages.deleteUsageRecordSubtitle },
                icon: ICON_MAP.DELETE,
                confirmationMessage: { ...subItemUsageRecordMessages.confirmDeleteUsageRecord },
            }}
            callbackButton={{
                variant: 'destructiveLight',
                buttonTitle: subItemUsageRecordMessages.deleteUsageRecord,
                callbackFn: () => {
                    deleteUsageRecord({ usageRecordId: selectedUsageRecord.id });
                    setIsModalOpen(false);
                },
            }}
            children={children}
        />
    );
}

export default DeleteSubscriptionItemUsageRecordFormModal;
